"use client";

import { isVideoContent } from "@/constants/routes";

export const PLAY_CONTEXT_STORAGE_KEY = "hotple:play-context";

const PLAY_CONTEXT_TTL_MS = 30 * 60 * 1000;

const unique = (items) => [...new Set(items.filter(Boolean))];

const getPostId = (post) => post?.id || post?._id || "";

const getPostPublicId = (post) => post?.publicId || "";

const isPlayableContextContent = (post) => isVideoContent(post) && post?.isPublic !== false;

const getCurrentSourcePath = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search || ""}`;
};

const normalizeContext = (context) => {
  if (!context || typeof context !== "object") return null;

  const feedIds = unique(Array.isArray(context.feedIds) ? context.feedIds : []);
  const feedPublicIds = unique(
    Array.isArray(context.feedPublicIds) ? context.feedPublicIds : []
  );
  const initialVideoId = context.initialVideoId || "";
  const initialPublicId = context.initialPublicId || "";

  if (!initialVideoId && !initialPublicId && feedIds.length === 0) return null;

  return {
    contextType: context.contextType || "related",
    initialVideoId,
    initialPublicId,
    feedIds,
    feedPublicIds,
    query: context.query || "",
    placeId: context.placeId || "",
    userId: context.userId || "",
    username: context.username || "",
    tag: context.tag || "",
    categoryId: context.categoryId || "",
    category: context.category || "",
    cursor: context.cursor || "",
    sourcePath: context.sourcePath || getCurrentSourcePath(),
    sourceParams: context.sourceParams || {},
    savedAt: Number(context.savedAt) || Date.now(),
  };
};

export const buildPlayContext = ({
  contextType = "related",
  initialVideo,
  feed = [],
  sourcePath,
  sourceParams,
  query,
  placeId,
  userId,
  username,
  tag,
  categoryId,
  category,
  cursor,
} = {}) => {
  const videoFeed = Array.isArray(feed) ? feed.filter(isPlayableContextContent) : [];
  const initialVideoId = getPostId(initialVideo);
  const initialPublicId = getPostPublicId(initialVideo);
  const feedIds = unique([initialVideoId, ...videoFeed.map(getPostId)]);
  const feedPublicIds = unique([initialPublicId, ...videoFeed.map(getPostPublicId)]);

  return normalizeContext({
    contextType,
    initialVideoId,
    initialPublicId,
    feedIds,
    feedPublicIds,
    query,
    placeId:
      placeId ||
      initialVideo?.locationTag ||
      initialVideo?.place?.id ||
      initialVideo?.placeUniqueNumber ||
      "",
    userId: userId || initialVideo?.userId || initialVideo?.user?.id || "",
    username: username || initialVideo?.user?.username || "",
    tag: tag || initialVideo?.tags?.[0] || "",
    categoryId: categoryId || initialVideo?.categoryId || "",
    category: category || initialVideo?.category?.name || "",
    cursor,
    sourcePath: sourcePath || getCurrentSourcePath(),
    sourceParams,
    savedAt: Date.now(),
  });
};

export const savePlayContext = (context) => {
  if (typeof window === "undefined") return null;

  const normalizedContext = normalizeContext(context);
  if (!normalizedContext) return null;

  try {
    window.sessionStorage?.setItem(
      PLAY_CONTEXT_STORAGE_KEY,
      JSON.stringify(normalizedContext)
    );

    window.history.replaceState(
      {
        ...(window.history.state || {}),
        hotplePlayContext: normalizedContext,
      },
      "",
      window.location.href
    );
  } catch (_) {
    return normalizedContext;
  }

  return normalizedContext;
};

const readSessionContext = () => {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.sessionStorage?.getItem(PLAY_CONTEXT_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (_) {
    return null;
  }
};

const contextMatchesVideo = (context, restorePostId) => {
  if (!restorePostId) return true;

  return (
    context.initialVideoId === restorePostId ||
    context.initialPublicId === restorePostId ||
    context.feedIds.includes(restorePostId) ||
    context.feedPublicIds.includes(restorePostId)
  );
};

export const getStoredPlayContext = (restorePostId) => {
  if (typeof window === "undefined") return null;
  if (!restorePostId && window.location.pathname === "/play") return null;

  const candidates = [
    window.history.state?.hotplePlayContext,
    readSessionContext(),
  ];

  for (const candidate of candidates) {
    const context = normalizeContext(candidate);
    if (!context) continue;
    if (Date.now() - context.savedAt > PLAY_CONTEXT_TTL_MS) continue;
    if (!contextMatchesVideo(context, restorePostId)) continue;
    return context;
  }

  return null;
};

export const buildPlayFeedParams = (
  context,
  { page = 1, limit = 10, restorePostId } = {}
) => ({
  page,
  limit,
  contextType: context?.contextType || "related",
  initialVideoId: restorePostId || context?.initialVideoId || "",
  initialPublicId: context?.initialPublicId || "",
  feedIds: context?.feedIds?.join(",") || "",
  query: context?.query || "",
  placeId: context?.placeId || "",
  userId: context?.userId || "",
  username: context?.username || "",
  tag: context?.tag || "",
  categoryId: context?.categoryId || "",
  category: context?.category || "",
  cursor: context?.cursor || "",
});

export { isPlayableContextContent };
